(function () {
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartToggles = document.querySelectorAll('[data-cart-toggle]');
  const productCache = new Map();
  const prefetching = new Set();

  function openCart() {
    if (cartDrawer && typeof cartDrawer.showModal === 'function') {
      cartDrawer.showModal();
    }
  }

  function closeCart() {
    if (cartDrawer && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    }
  }

  let lastCartToggle = null;
  let cartBusy = false;

  function updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count ?? 0);
    });
  }

  function announce(message) {
    let live = document.getElementById('cart-live-region');
    if (!live) {
      live = document.createElement('div');
      live.id = 'cart-live-region';
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = message;
  }

  async function fetchCart() {
    const res = await fetch(window.Shopify?.routes?.cart_url ? `${window.Shopify.routes.cart_url}.js` : '/cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Cart unavailable');
    return res.json();
  }

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    const sectionId = cartDrawer.dataset.sectionId;
    const panel = cartDrawer.querySelector('[data-cart-drawer-panel]');
    if (!sectionId || !panel) return;

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${sectionId}`);
      if (!res.ok) return;
      const data = await res.json();
      const html = data[sectionId];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextPanel = doc.querySelector('[data-cart-drawer-panel]');
      if (nextPanel) panel.innerHTML = nextPanel.innerHTML;
    } catch (err) {
      console.warn('Cart drawer refresh failed', err);
    }
  }

  async function syncCartState() {
    try {
      const cart = await fetchCart();
      updateCartCounts(cart.item_count);
      await refreshCartDrawer();
      return cart;
    } catch (err) {
      console.warn('Cart sync failed', err);
      return null;
    }
  }

  async function addToCart(variantId, quantity = 1) {
    if (!variantId || cartBusy) return false;
    cartBusy = true;
    try {
      const res = await fetch(window.Shopify?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) || 1 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data.description || data.message || 'Could not add to cart. Please try again.';
        announce(message);
        return false;
      }
      await syncCartState();
      announce('Added to cart');
      return true;
    } catch (err) {
      announce('Could not add to cart. Check your connection and try again.');
      return false;
    } finally {
      cartBusy = false;
    }
  }

  async function changeCartLine(lineKey, quantity) {
    if (!lineKey || cartBusy) return false;
    cartBusy = true;
    try {
      const res = await fetch(window.Shopify?.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: Number(quantity) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        announce(data.description || data.message || 'Could not update cart.');
        return false;
      }
      updateCartCounts(data.item_count);
      await refreshCartDrawer();
      return true;
    } catch (err) {
      announce('Could not update cart. Please try again.');
      return false;
    } finally {
      cartBusy = false;
    }
  }

  cartToggles.forEach((btn) => btn.addEventListener('click', (e) => {
    if (cartDrawer) {
      e.preventDefault();
      lastCartToggle = btn;
      openCart();
    }
  }));

  if (cartDrawer) {
    cartDrawer.addEventListener('close', () => {
      if (lastCartToggle && typeof lastCartToggle.focus === 'function') lastCartToggle.focus();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.open) {
        e.preventDefault();
        closeCart();
      }
    });
  }

  document.addEventListener('click', async (e) => {
    if (!(e.target instanceof Element)) return;

    const closeBtn = e.target.closest('[data-cart-close]');
    if (closeBtn) {
      e.preventDefault();
      closeCart();
      return;
    }

    const qtyBtn = e.target.closest('[data-cart-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const line = qtyBtn.closest('[data-cart-line]');
      const input = line?.querySelector('[data-cart-qty-input]');
      if (!input) return;
      const delta = parseInt(qtyBtn.dataset.delta, 10) || 0;
      const nextQty = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
      await changeCartLine(qtyBtn.dataset.lineKey, nextQty);
      return;
    }

    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      await changeCartLine(removeBtn.dataset.lineKey, 0);
      return;
    }

    const upsellAdd = e.target.closest('[data-cart-upsell-add]');
    if (upsellAdd) {
      e.preventDefault();
      if (upsellAdd.disabled || upsellAdd.getAttribute('aria-disabled') === 'true') return;
      const ok = await addToCart(parseInt(upsellAdd.dataset.variantId, 10), 1);
      if (ok) openCart();
      else {
        const err = upsellAdd.closest('[data-cart-upsell-item]')?.querySelector('[data-upsell-error]');
        if (err) {
          err.hidden = false;
          err.textContent = 'Could not add to cart. Please try again or contact the store.';
        }
      }
      return;
    }

    const bundleAdd = e.target.closest('[data-bundle-add-selected]');
    if (bundleAdd) {
      e.preventDefault();
      const wrap = bundleAdd.closest('[data-bundle-suggest]');
      const checks = wrap?.querySelectorAll('[data-bundle-check]:checked') || [];
      for (const check of checks) {
        await addToCart(parseInt(check.value, 10), 1);
      }
      openCart();
    }
  });

  document.addEventListener('change', async (e) => {
    if (!(e.target instanceof Element)) return;
    const qtyInput = e.target.closest('[data-cart-qty-input]');
    if (!qtyInput) return;
    const qty = Math.max(0, parseInt(qtyInput.value, 10) || 0);
    await changeCartLine(qtyInput.dataset.lineKey, qty);
  });

  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (menuToggle && mobileNav) {
    const closeMobileNav = () => {
      mobileNav.hidden = true;
      menuToggle.setAttribute('aria-expanded', 'false');
    };
    const openMobileNav = () => {
      mobileNav.hidden = false;
      menuToggle.setAttribute('aria-expanded', 'true');
    };
    menuToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      if (mobileNav.hidden) openMobileNav();
      else closeMobileNav();
    });
    document.addEventListener('click', (e) => {
      if (mobileNav.hidden) return;
      if (!mobileNav.contains(e.target) && !menuToggle.contains(e.target)) closeMobileNav();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !mobileNav.hidden) closeMobileNav();
    });
    mobileNav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeMobileNav);
    });
  }

  const tabsRoot = document.querySelector('[data-featured-tabs]');
  if (tabsRoot) {
    const tabs = tabsRoot.querySelectorAll('[data-tab]');
    const panels = tabsRoot.querySelectorAll('[data-panel]');
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.dataset.tab;
        tabs.forEach((t) => {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        panels.forEach((p) => {
          const active = p.dataset.panel === id;
          p.classList.toggle('is-active', active);
          p.hidden = !active;
        });
      });
    });
  }

  const megaToggle = document.querySelector('[data-categories-toggle]');
  const megaMenu = document.querySelector('[data-mega-menu]');
  if (megaToggle && megaMenu) {
    const closeMega = () => {
      megaMenu.hidden = true;
      megaToggle.setAttribute('aria-expanded', 'false');
    };
    const openMega = () => {
      megaMenu.hidden = false;
      megaToggle.setAttribute('aria-expanded', 'true');
    };
    megaToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      if (megaMenu.hidden) openMega();
      else closeMega();
    });
    document.addEventListener('click', (e) => {
      if (megaMenu.hidden) return;
      if (!megaMenu.contains(e.target) && !megaToggle.contains(e.target)) closeMega();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !megaMenu.hidden) closeMega();
    });
    megaMenu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeMega);
    });
  }

  const stickyAtc = document.querySelector('[data-sticky-atc]');
  const stickyBtn = document.querySelector('[data-sticky-add]');
  const productForm = document.querySelector('[data-product-form]');
  const productJsonEl = document.querySelector('[data-product-json]');
  const sellabilityEl = document.querySelector('[data-variant-sellability]');
  if (productForm && productJsonEl) {
    let productData = null;
    let sellabilityMap = { in_store: false, variants: {} };
    try { productData = JSON.parse(productJsonEl.textContent); } catch (e) {}
    try {
      if (sellabilityEl?.textContent) sellabilityMap = JSON.parse(sellabilityEl.textContent);
    } catch (e) {}

    const formatMoney = (cents) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
      }
      return `R ${(Number(cents) / 100).toFixed(2)}`;
    };

    const variantInput = productForm.querySelector('[data-product-variant-id]');
    const optionSelects = productForm.querySelectorAll('[data-product-option]');
    const addBtn = productForm.querySelector('[data-product-add]');
    const qtyField = productForm.querySelector('[name="quantity"]');
    const priceWrap = document.querySelector('[data-product-price]');
    const stockEl = document.querySelector('[data-product-stock]');
    const skuEl = document.querySelector('[data-product-sku]');
    const formError = productForm.querySelector('[data-product-form-error]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const productInStore = sellabilityMap.in_store === true;

    const sellStateFor = (variant) => {
      if (!variant) return { available: false, can_sell: false, in_store: productInStore, low_stock: false, stock_qty: null };
      const mapped = sellabilityMap.variants?.[String(variant.id)];
      if (mapped) {
        return {
          available: mapped.available === true,
          can_sell: mapped.can_sell === true,
          in_store: mapped.in_store === true || productInStore,
          low_stock: mapped.low_stock === true,
          stock_qty: mapped.stock_qty,
        };
      }
      return {
        available: variant.available === true,
        can_sell: variant.available === true,
        in_store: productInStore,
        low_stock: false,
        stock_qty: null,
      };
    };

    const findVariant = () => {
      if (!productData?.variants?.length) return null;
      if (productData.variants.length === 1) return productData.variants[0];
      const selected = Array.from(optionSelects).map((select) => select.value);
      return productData.variants.find((variant) =>
        selected.every((value, index) => variant[`option${index + 1}`] === value)
      ) || null;
    };

    const renderAvailability = (state) => {
      if (!stockEl) return;
      stockEl.replaceChildren();
      if (!state.can_sell) {
        const out = document.createElement('span');
        out.className = 'product-main__stock--out';
        out.textContent = 'Sold out';
        stockEl.append(out);
        return;
      }
      if (state.in_store) {
        stockEl.textContent = 'Available in store';
        return;
      }
      if (state.low_stock && state.stock_qty != null && Number(state.stock_qty) > 0) {
        stockEl.textContent = `Only ${Number(state.stock_qty)} left`;
      } else {
        stockEl.textContent = 'Available';
      }
    };

    const syncVariant = () => {
      const variant = findVariant();
      if (!variant || !variantInput) return;
      variantInput.value = variant.id;
      const state = sellStateFor(variant);
      if (priceWrap) {
        const compare = variant.compare_at_price;
        priceWrap.innerHTML = compare > variant.price
          ? `<div class="price" data-price><s class="price__compare">${formatMoney(compare)}</s> <span class="price__current">${formatMoney(variant.price)}</span></div>`
          : `<div class="price" data-price><span class="price__current">${formatMoney(variant.price)}</span></div>`;
      }
      if (stickyPrice) {
        stickyPrice.innerHTML = `<span class="price__current">${formatMoney(variant.price)}</span>`;
      }
      if (skuEl) {
        if (variant.sku) {
          skuEl.hidden = false;
          skuEl.textContent = `SKU: ${variant.sku}`;
        } else {
          skuEl.hidden = true;
          skuEl.textContent = '';
        }
      }
      renderAvailability(state);
      if (qtyField) qtyField.disabled = !state.can_sell;
      if (addBtn) {
        addBtn.disabled = !state.can_sell;
        addBtn.setAttribute('aria-disabled', state.can_sell ? 'false' : 'true');
        addBtn.textContent = state.can_sell ? 'Add to cart' : 'Sold out';
      }
      if (stickyBtn) {
        stickyBtn.disabled = !state.can_sell;
        stickyBtn.setAttribute('aria-disabled', state.can_sell ? 'false' : 'true');
      }
    };

    optionSelects.forEach((select) => select.addEventListener('change', syncVariant));
    syncVariant();

    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (formError) {
        formError.hidden = true;
        formError.textContent = '';
      }
      const variant = findVariant();
      const state = sellStateFor(variant);
      if (!state.can_sell) {
        if (formError) {
          formError.hidden = false;
          formError.textContent = 'This option is unavailable. Choose another variant.';
        }
        return;
      }
      const qty = parseInt(productForm.querySelector('[name="quantity"]')?.value, 10) || 1;
      const ok = await addToCart(variant.id, qty);
      if (ok) openCart();
      else if (formError) {
        formError.hidden = false;
        formError.textContent = 'Could not add to cart. Please try again or contact the store.';
      }
    });
  }

  if (stickyAtc && productForm) {
    const observer = new IntersectionObserver(([entry]) => {
      stickyAtc.hidden = entry.isIntersecting;
    }, { threshold: 0 });
    observer.observe(productForm);
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        if (typeof productForm.requestSubmit === 'function') productForm.requestSubmit();
        else productForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }
  }

  const recSection = document.querySelector('product-recommendations');
  if (recSection) {
    const url = recSection.dataset.url;
    if (url) {
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('product-recommendations');
          if (content && content.innerHTML.trim()) {
            recSection.innerHTML = content.innerHTML;
          }
        })
        .catch(() => {});
    }
  }
  const socialProof = document.querySelector('[data-social-proof]');
  const socialData = document.querySelector('[data-social-proof-data]');
  if (socialProof && socialData) {
    let items = [];
    try { items = JSON.parse(socialData.textContent); } catch (e) {}
    const textEl = socialProof.querySelector('[data-social-proof-text]');
    const closeBtn = socialProof.querySelector('[data-social-proof-close]');
    let idx = 0;
    const show = () => {
      if (!items.length || !textEl) return;
      const item = items[idx % items.length];
      textEl.innerHTML = `<strong>${item.customer_name}</strong> purchased: ${item.product_title}<br>${item.time_ago || ''}`;
      socialProof.hidden = false;
      idx += 1;
    };
    if (closeBtn) closeBtn.addEventListener('click', () => { socialProof.hidden = true; });
    setTimeout(show, 4000);
    setInterval(show, 12000);
  }

  document.querySelectorAll('[data-policy-trigger]').forEach((trigger) => {
    const id = trigger.dataset.policyTrigger;
    const dialog = document.getElementById(`policy-dialog-${id}`);
    if (!dialog) return;
    trigger.addEventListener('click', () => {
      if (typeof dialog.showModal === 'function') dialog.showModal();
    });
    dialog.querySelectorAll('[data-policy-close]').forEach((btn) => {
      btn.addEventListener('click', () => dialog.close());
    });
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const mainImg = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    const lightboxImg = gallery.querySelector('[data-gallery-lightbox-img]');
    const counter = gallery.querySelector('[data-gallery-counter]');
    const prevBtn = gallery.querySelector('[data-gallery-prev]');
    const nextBtn = gallery.querySelector('[data-gallery-next]');
    const openBtn = gallery.querySelector('[data-gallery-open]');
    const closeBtn = gallery.querySelector('[data-gallery-close]');
    const stage = gallery.querySelector('[data-gallery-stage]');
    const dataEl = gallery.querySelector('[data-gallery-images]');

    if (!mainImg || !dataEl) return;

    let images = [];
    let activeIndex = 0;
    try { images = JSON.parse(dataEl.textContent); } catch (e) {}

    const setMain = (index) => {
      if (!images[index]) return;
      activeIndex = index;
      const img = images[index];
      mainImg.src = img.src;
      if (img.srcset) mainImg.srcset = img.srcset;
      mainImg.sizes = mainImg.sizes || '(min-width: 900px) min(50vw, 640px), 100vw';
      if (img.width) mainImg.width = img.width;
      if (img.height) mainImg.height = img.height;
      mainImg.alt = img.alt || '';
      thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    const showLightbox = (index) => {
      if (!lightbox || !lightboxImg || !images[index]) return;
      activeIndex = index;
      const img = images[index];
      lightboxImg.src = img.src;
      lightboxImg.alt = img.alt || '';
      if (img.width) lightboxImg.width = img.width;
      if (img.height) lightboxImg.height = img.height;
      if (counter) counter.textContent = `${index + 1} / ${images.length}`;
      if (typeof lightbox.showModal === 'function') lightbox.showModal();
    };

    const stepLightbox = (delta) => {
      const next = (activeIndex + delta + images.length) % images.length;
      showLightbox(next);
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => setMain(index));
      thumb.addEventListener('dblclick', () => showLightbox(index));
    });

    if (openBtn) openBtn.addEventListener('click', () => showLightbox(activeIndex));
    if (closeBtn && lightbox) closeBtn.addEventListener('click', () => lightbox.close());
    if (lightbox) {
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox) lightbox.close();
      });
    }
    if (prevBtn) prevBtn.addEventListener('click', () => stepLightbox(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => stepLightbox(1));

    if (stage && lightbox) {
      let touchStartX = 0;
      stage.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        const delta = e.changedTouches[0].screenX - touchStartX;
        if (Math.abs(delta) < 40) return;
        stepLightbox(delta > 0 ? -1 : 1);
      }, { passive: true });
    }

    document.addEventListener('keydown', (e) => {
      if (!lightbox || !lightbox.open) return;
      if (e.key === 'ArrowLeft') stepLightbox(-1);
      if (e.key === 'ArrowRight') stepLightbox(1);
      if (e.key === 'Escape') lightbox.close();
    });
  });

  const quickViewModal = document.querySelector('[data-quick-view-modal]');
  if (quickViewModal) {
    const content = quickViewModal.querySelector('[data-quick-view-content]');
    const loading = quickViewModal.querySelector('[data-quick-view-loading]');
    const media = quickViewModal.querySelector('[data-quick-view-media]');
    const vendor = quickViewModal.querySelector('[data-quick-view-vendor]');
    const title = quickViewModal.querySelector('[data-quick-view-title]');
    const priceEl = quickViewModal.querySelector('[data-quick-view-price]');
    const stockEl = quickViewModal.querySelector('[data-quick-view-stock]');
    const descEl = quickViewModal.querySelector('[data-quick-view-description]');
    const variantsWrap = quickViewModal.querySelector('[data-quick-view-variants]');
    const variantInput = quickViewModal.querySelector('[data-quick-view-variant-id]');
    const form = quickViewModal.querySelector('[data-quick-view-form]');
    const link = quickViewModal.querySelector('[data-quick-view-link]');
    const closeBtn = quickViewModal.querySelector('[data-quick-view-close]');
    const formError = quickViewModal.querySelector('[data-quick-view-error]');
    let currentProduct = null;
    let currentInStore = false;
    let activeHandle = null;
    let lastTrigger = null;

    const formatMoney = (cents) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
      }
      return `R ${(cents / 100).toFixed(2)}`;
    };

    const renderPriceHtml = ({ retailCents, compareCents }) => {
      if (compareCents > retailCents) {
        return `<s class="price__compare">${formatMoney(compareCents)}</s> <span class="price__current">${formatMoney(retailCents)}</span>`;
      }
      return `<span class="price__current">${formatMoney(retailCents)}</span>`;
    };

    const stockLabel = ({ canSell, available, inStore, lowStock, stockQty }) => {
      if (!canSell) {
        return { text: 'Sold out', color: '#c0392b' };
      }
      if (inStore) {
        return { text: 'Available in store', color: 'var(--color-success)' };
      }
      if (lowStock && stockQty != null && Number(stockQty) > 0) {
        return { text: `Only ${Number(stockQty)} left`, color: 'var(--color-success)' };
      }
      if (available || canSell) {
        return { text: 'Available', color: 'var(--color-success)' };
      }
      return { text: 'Sold out', color: '#c0392b' };
    };

    const setAddControls = ({ canSell }) => {
      const addBtn = quickViewModal.querySelector('[data-quick-view-add]');
      const qtyInput = form?.querySelector('[name="quantity"]');
      if (qtyInput) qtyInput.disabled = !canSell;
      if (addBtn) {
        addBtn.hidden = false;
        addBtn.disabled = !canSell;
        addBtn.setAttribute('aria-disabled', canSell ? 'false' : 'true');
        addBtn.textContent = canSell ? 'Add to cart' : 'Sold out';
      }
    };

    const prefetchProduct = (handle) => {
      if (!handle || productCache.has(handle) || prefetching.has(handle)) return;
      prefetching.add(handle);
      fetch(`/products/${handle}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .then((product) => {
          if (product) productCache.set(handle, product);
        })
        .catch(() => {})
        .finally(() => prefetching.delete(handle));
    };

    const paintQuickView = (payload) => {
      if (media) {
        media.replaceChildren();
        if (payload.image) {
          const img = document.createElement('img');
          img.src = payload.image;
          img.alt = payload.title || '';
          img.fetchPriority = 'high';
          media.appendChild(img);
        }
      }
      if (vendor) {
        vendor.textContent = payload.vendor || '';
        vendor.hidden = !payload.vendor;
      }
      if (title) title.textContent = payload.title;
      if (priceEl) {
        priceEl.innerHTML = renderPriceHtml({
          retailCents: payload.price,
          compareCents: payload.compare,
        });
      }
      if (stockEl) {
        const label = stockLabel({
          canSell: payload.canSell,
          available: payload.available,
          inStore: payload.inStore,
          lowStock: payload.lowStock,
          stockQty: payload.stock,
        });
        stockEl.textContent = label.text;
        stockEl.style.color = label.color;
      }
      if (descEl) descEl.textContent = payload.description || '';
      if (link) link.href = payload.url;
      if (variantInput) variantInput.value = payload.variantId || '';
      setAddControls({ canSell: payload.canSell });
    };

    const payloadFromTrigger = (trigger) => ({
      title: trigger.dataset.qvTitle || '',
      vendor: trigger.dataset.qvVendor || '',
      price: parseInt(trigger.dataset.qvPrice, 10) || 0,
      compare: parseInt(trigger.dataset.qvCompare, 10) || 0,
      image: trigger.dataset.qvImage || '',
      url: trigger.dataset.qvUrl || `/products/${trigger.dataset.quickView}`,
      variantId: parseInt(trigger.dataset.qvVariant, 10) || '',
      available: trigger.dataset.qvAvailable === 'true',
      canSell: trigger.dataset.qvCanSell === 'true',
      stock: trigger.dataset.qvStock !== undefined && trigger.dataset.qvStock !== ''
        ? parseInt(trigger.dataset.qvStock, 10)
        : null,
      lowStock: trigger.dataset.qvLowStock === 'true',
      inStore: trigger.dataset.qvInstore === 'true',
      description: '',
    });

    const sellStateFromVariant = (variant) => {
      const available = variant?.available === true;
      const canSell = available;
      const qtyRaw = variant?.inventory_quantity;
      const stockQty = qtyRaw == null || qtyRaw === '' ? null : Number(qtyRaw);
      const tracks = variant?.inventory_management != null && variant.inventory_management !== '';
      const lowStock = tracks && stockQty != null && stockQty > 0 && stockQty <= 10;
      return { available, canSell, inStore: currentInStore, lowStock, stockQty };
    };

    const setVariant = (variantId) => {
      if (!currentProduct || !variantInput) return;
      const variant = currentProduct.variants.find((v) => v.id === variantId);
      if (!variant) return;
      variantInput.value = String(variant.id);
      const state = sellStateFromVariant(variant);
      if (priceEl) {
        priceEl.innerHTML = renderPriceHtml({
          retailCents: variant.price,
          compareCents: variant.compare_at_price || 0,
        });
      }
      if (stockEl) {
        const label = stockLabel({
          canSell: state.canSell,
          available: state.available,
          inStore: state.inStore,
          lowStock: state.lowStock,
          stockQty: state.stockQty,
        });
        stockEl.textContent = label.text;
        stockEl.style.color = label.color;
      }
      setAddControls({ canSell: state.canSell });
    };

    const hydrateQuickView = (product) => {
      currentProduct = product;
      if (media) {
        media.replaceChildren();
        const imgSrc = product.featured_image || product.images?.[0];
        if (imgSrc) {
          const img = document.createElement('img');
          img.src = imgSrc;
          img.alt = product.title || '';
          img.fetchPriority = 'high';
          media.appendChild(img);
        }
      }
      if (vendor) {
        vendor.textContent = product.vendor || '';
        vendor.hidden = !product.vendor;
      }
      if (title) title.textContent = product.title;
      if (descEl) {
        const text = product.body_html || product.description || '';
        const plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        descEl.textContent = plain.length > 320 ? `${plain.slice(0, 320)}…` : plain;
      }
      if (link) link.href = `/products/${product.handle}`;

      if (variantsWrap) {
        variantsWrap.replaceChildren();
        if (product.variants.length > 1) {
          variantsWrap.hidden = false;
          const label = document.createElement('label');
          label.textContent = 'Options';
          const select = document.createElement('select');
          select.setAttribute('data-quick-view-select', '');
          product.variants.forEach((variant) => {
            const opt = document.createElement('option');
            opt.value = variant.id;
            const state = sellStateFromVariant(variant);
            opt.textContent = state.canSell
              ? variant.title
              : `${variant.title} — Sold out`;
            select.appendChild(opt);
          });
          select.addEventListener('change', () => setVariant(parseInt(select.value, 10)));
          label.appendChild(select);
          variantsWrap.appendChild(label);
          const preferredId = lastTrigger?.dataset?.qvVariant
            ? parseInt(lastTrigger.dataset.qvVariant, 10)
            : parseInt(select.value, 10);
          const match = product.variants.find((v) => v.id === preferredId);
          select.value = String(match ? match.id : product.variants[0].id);
          setVariant(parseInt(select.value, 10));
        } else {
          variantsWrap.hidden = true;
          setVariant(product.variants[0].id);
        }
      } else if (product.variants?.[0]) {
        setVariant(product.variants[0].id);
      }
    };

    const openQuickView = async (trigger) => {
      const handle = trigger?.dataset?.quickView;
      if (!handle) return;
      activeHandle = handle;
      lastTrigger = trigger;
      currentInStore = trigger.dataset.qvInstore === 'true';
      if (formError) {
        formError.hidden = true;
        formError.textContent = '';
      }

      const instant = payloadFromTrigger(trigger);
      paintQuickView(instant);
      loading.hidden = true;
      content.hidden = false;
      if (typeof quickViewModal.showModal === 'function') quickViewModal.showModal();

      let product = productCache.get(handle);
      if (!product) {
        prefetchProduct(handle);
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error('Product not found');
          product = await res.json();
          productCache.set(handle, product);
        } catch (err) {
          if (activeHandle === handle) loading.textContent = 'Unable to load product.';
          return;
        }
      }

      if (activeHandle !== handle) return;
      hydrateQuickView(product);
    };

    document.addEventListener('mouseenter', (e) => {
      if (!(e.target instanceof Element)) return;
      const trigger = e.target.closest('[data-quick-view]');
      if (trigger) prefetchProduct(trigger.dataset.quickView);
    }, true);

    document.addEventListener('focusin', (e) => {
      if (!(e.target instanceof Element)) return;
      const trigger = e.target.closest('[data-quick-view]');
      if (trigger) prefetchProduct(trigger.dataset.quickView);
    });

    document.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      const trigger = e.target.closest('[data-quick-view]');
      if (!trigger) return;
      e.preventDefault();
      openQuickView(trigger);
    });

    if (closeBtn) closeBtn.addEventListener('click', () => quickViewModal.close());
    quickViewModal.addEventListener('click', (e) => {
      if (e.target === quickViewModal) quickViewModal.close();
    });
    quickViewModal.addEventListener('close', () => {
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    });

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (formError) {
          formError.hidden = true;
          formError.textContent = '';
        }
        const fd = new FormData(form);
        const variantId = fd.get('id');
        const quantity = fd.get('quantity') || 1;
        if (!variantId) {
          if (formError) {
            formError.hidden = false;
            formError.textContent = 'Select a product option before adding to cart.';
          }
          return;
        }
        const addBtn = quickViewModal.querySelector('[data-quick-view-add]');
        if (addBtn?.disabled) {
          if (formError) {
            formError.hidden = false;
            formError.textContent = 'This option is sold out.';
          }
          return;
        }
        const ok = await addToCart(variantId, quantity);
        if (ok) {
          quickViewModal.close();
          openCart();
        } else if (formError) {
          formError.hidden = false;
          formError.textContent = 'Could not add to cart. Please try again or contact the store.';
        }
      });
    }
  }

  /* Predictive search — DOM APIs only; never interpolate API/user strings into innerHTML */
  document.querySelectorAll('[data-predictive-search]').forEach((root) => {
    const input = root.querySelector('[data-predictive-search-input]');
    const results = root.querySelector('[data-predictive-search-results]');
    if (!input || !results) return;
    let timer = null;
    let activeIndex = -1;

    const hide = () => {
      results.hidden = true;
      results.replaceChildren();
      activeIndex = -1;
    };

    const safeUrl = (raw) => {
      if (!raw || typeof raw !== 'string') return null;
      try {
        const base = window.location.origin;
        const url = new URL(raw, base);
        if (url.origin !== base) return null;
        if (!url.pathname.startsWith('/')) return null;
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (err) {
        return null;
      }
    };

    const render = (items, terms) => {
      results.replaceChildren();
      if (!items.length) {
        const empty = document.createElement('div');
        empty.className = 'header__predictive-empty';
        empty.textContent = `No matches for “${terms}”. Try another product name, brand or SKU.`;
        results.appendChild(empty);
        results.hidden = false;
        return;
      }
      items.forEach((item, index) => {
        const href = safeUrl(item.url);
        if (!href) return;
        const a = document.createElement('a');
        a.setAttribute('role', 'option');
        a.href = href;
        a.setAttribute('aria-selected', 'false');
        a.dataset.index = String(index);

        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.width = 48;
          img.height = 48;
          img.loading = 'lazy';
          a.appendChild(img);
        } else {
          a.appendChild(document.createElement('span'));
        }

        const textWrap = document.createElement('span');
        const titleEl = document.createElement('span');
        titleEl.className = 'header__predictive-title';
        titleEl.textContent = item.title || '';
        const metaEl = document.createElement('span');
        metaEl.className = 'header__predictive-meta';
        metaEl.textContent = item.vendor ? `${item.vendor} · ${item.price || ''}` : (item.price || '');
        textWrap.append(titleEl, metaEl);
        a.appendChild(textWrap);
        results.appendChild(a);
      });
      results.hidden = false;
    };

    const search = async (terms) => {
      if (terms.length < 2) { hide(); return; }
      try {
        const suggestUrl = `/search/suggest.json?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=6`;
        const res = await fetch(suggestUrl, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('search failed');
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        const items = products.map((p) => ({
          title: String(p.title || ''),
          url: String(p.url || ''),
          vendor: String(p.vendor || ''),
          price: String(p.price || ''),
          image: typeof p.image === 'string' ? p.image : '',
        }));
        render(items, terms);
      } catch (err) {
        hide();
      }
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => search(input.value.trim()), 200);
    });
    input.addEventListener('keydown', (e) => {
      const options = Array.from(results.querySelectorAll('[role="option"]'));
      if (e.key === 'Escape') { hide(); return; }
      if (!options.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, options.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        options[activeIndex].click();
        return;
      } else {
        return;
      }
      options.forEach((opt, i) => opt.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false'));
    });
    document.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      if (!root.contains(e.target)) hide();
    });
  });

})();
